// Practice-length helpers — the 1-5 minute slider on the three Playground
// practices (The Instrument, Body Language, Archetype). The user picks a
// duration; the story is sized to it at the speaker's own preferred speaking
// rate, cues are scheduled across the full take, and the practice auto-stops at
// the chosen length.
//
// See `wiki/product-vision.md` §"The six practices" — the three Playground
// practices all use short, cue-driven takes; making the duration adjustable
// without bloating it to Mastery's 3-15min range keeps the playground
// playful while letting users tune the cadence to their attention span.
//
// Persisted per-practice in localStorage so a user's preferred length
// survives refresh + navigation.
//
// ── FRONTEND-OWNED ON PURPOSE — do NOT derive these bounds from the backend ──
// The house rule is that hard numbers come from the backend; this is the
// documented exception (wiki/feature-contract-reads.md §1). The nearest-looking
// backend numbers are `PracticeImproviseEndpoint`'s private
// `RiffMin/MaxDurationSeconds` (60 / 300) — they only OVERLAP these bounds, they
// are not their source. Those constants clamp **Riff specifically**: Riff is
// intentionally a 1-5 minute one-word improv, while the other Playground
// practices' slider range is a UI pacing choice this file owns and may diverge
// from Riff at any time. Wiring the slider to the Riff clamp would silently
// couple every Playground practice's UI to one practice's server-side guard, so
// a future single-source sweep must leave this alone.

import { fromStore } from 'svelte/store';
import {
  DefaultPreferredWpm,
  MinRiffCues,
  MinTargetWords,
  RiffSecondsPerCue,
} from '@voicedojo/backend-types';
import { clampDuration } from '$lib/utils/time/clampDuration';
import { auth } from '$lib/stores/auth';
import type { PracticeSlug } from '$lib/practice/practiceSlug';

/**
 * The speaking rate the AI content generator sizes stories against — the
 * speaker's own `PreferredWpm`. A guest has no setting and falls back to the
 * backend-declared default, the same number the server's own sizing uses.
 */
const authState = fromStore(auth);

// Tracked when read in a reactive context, so a seed shown before whoami settles follows the profile in.
export function wordsPerMinute(): number {
  return authState.current.user?.preferredWpm ?? DefaultPreferredWpm;
}

/** Inclusive lower bound on the slider value (1 minute). */
export const MIN_PRACTICE_LENGTH_MINUTES = 1;

/** Inclusive upper bound on the slider value (5 minutes). */
export const MAX_PRACTICE_LENGTH_MINUTES = 5;

/** Default slider value used on first visit / when localStorage is blocked. */
export const DEFAULT_PRACTICE_LENGTH_MINUTES = 3;

/**
 * The Playground practices the slider is mounted on — the canonical FE practice
 * slugs (`practiceSlug.ts`), distinct from the backend `Practice` enum so we
 * don't accidentally scope a Mastery practice's storage key under here (those
 * have their own dropdown UI). Riff joins Instrument / Body Language / Archetype
 * as a 1-5 min Playground practice (one-word improv).
 */
export type PracticeLengthKey = PracticeSlug;

/**
 * Storage-key prefix. The slider persists per practice so changing the
 * Instrument slider doesn't move Body Language's slider too. Exported so a
 * test can scan the keys.
 */
export const PRACTICE_LENGTH_STORAGE_PREFIX = 'vd.practice.length.';

/** Builds the localStorage key for a given practice. */
export function practiceLengthStorageKey(practice: PracticeLengthKey): string {
  return `${PRACTICE_LENGTH_STORAGE_PREFIX}${practice}`;
}

/**
 * Reads the stored slider value for a practice, clamped to the valid range.
 * Returns the default when nothing is stored or the stored value is invalid
 * (a corrupted entry — non-numeric, NaN, out-of-range). SSR-safe: returns
 * the default when `window` is unavailable.
 */
export function readPracticeLength(practice: PracticeLengthKey): number {
  if (typeof window === 'undefined') return DEFAULT_PRACTICE_LENGTH_MINUTES;
  try {
    const raw = window.localStorage.getItem(practiceLengthStorageKey(practice));
    if (raw === null) return DEFAULT_PRACTICE_LENGTH_MINUTES;
    const parsed = Number.parseInt(raw, 10);
    return clampPracticeLength(parsed);
  } catch {
    return DEFAULT_PRACTICE_LENGTH_MINUTES;
  }
}

/**
 * Persists the slider value. No-ops on out-of-range input (defensive — a
 * caller shouldn't pass an invalid value but we clamp anyway). SSR-safe:
 * silently no-ops when `window` is unavailable.
 */
export function writePracticeLength(
  practice: PracticeLengthKey,
  minutes: number,
): void {
  if (typeof window === 'undefined') return;
  const clamped = clampPracticeLength(minutes);
  try {
    window.localStorage.setItem(
      practiceLengthStorageKey(practice),
      String(clamped),
    );
  } catch {
    // Private mode / quota — the in-memory slider still works.
  }
}

/**
 * Clamps an arbitrary number to the slider's valid integer range. Returns
 * the default when the input is non-finite or NaN. Delegates to the shared
 * {@link clampDuration} with the slider's 1–5 min constants.
 */
export function clampPracticeLength(minutes: number): number {
  return clampDuration(
    minutes,
    MIN_PRACTICE_LENGTH_MINUTES,
    MAX_PRACTICE_LENGTH_MINUTES,
    DEFAULT_PRACTICE_LENGTH_MINUTES,
  );
}

/**
 * Target word count at the CHOSEN rate — an explicit argument because the
 * speaker sets it per practice on the setup block, so reading the profile here
 * would discard that pick. Mirrors the server's `TargetWordCount`.
 */
export function targetWordsFor(durationMinutes: number, wpm: number): number {
  return Math.max(MinTargetWords, clampPracticeLength(durationMinutes) * wpm);
}

/** Riff's "I need help!" cue count for a take — the server's `RiffCueCount`. */
export function riffCueCountFor(durationMinutes: number): number {
  return Math.max(MinRiffCues, Math.round((durationMinutes * 60) / RiffSecondsPerCue));
}
